import React, { useState, useEffect, useRef } from "react";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import "../VerifyEmailA/VerifyEmail.css";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../constant";
import { useSearchParams } from "react-router-dom";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [timeLeft, setTimeLeft] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [email, setEmail] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const inputRefs = useRef([]);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const urlEmail = searchParams.get("email");

    if (urlEmail) {
      setEmail(urlEmail);
      return;
    }

    const storedEmail = localStorage.getItem("verifyEmail");
    if (!storedEmail) {
      navigate("/member-login");
      return;
    }
    setEmail(storedEmail);

    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [navigate]);

  useEffect(() => {
    if (timeLeft === 0) {
      setIsResendDisabled(false);
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  useEffect(() => {
    if (showSuccessPopup) {
      const timer = setTimeout(() => {
        setShowSuccessPopup(false);
        navigate("/member-login");
      }, 100000000000);

      return () => clearTimeout(timer);
    }
  }, [showSuccessPopup, navigate]);

  const handleChange = (index, e) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (isNaN(pastedData)) return;

    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      if (i < 6) {
        newOtp[i] = pastedData[i];
      }
    }
    setOtp(newOtp);

    const nextIndex = pastedData.length < 6 ? pastedData.length : 5;
    inputRefs.current[nextIndex].focus();
  };

  const handleResend = async () => {
    if (!isResendDisabled) {
      setIsLoading(true);
      setMessage({ text: "", type: "" });

      try {
        const response = await fetch(
          `${API_URL}/api/member/resend-email-verification`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ email }),
          }
        );

        const data = await response.json();

        if (response.ok) {
          setTimeLeft(60);
          setIsResendDisabled(true);
          setOtp(new Array(6).fill(""));

          if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
          }

          setMessage({
            text: "Verification code has been resent to your email",
            type: "success",
          });
        } else {
          setMessage({
            text:
              data.message || "Failed to resend code. Please try again.",
            type: "error",
          });
        }
      } catch (error) {
        setMessage({
          text: "Network error. Please check your connection and try again.",
          type: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleVerify = async () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      setMessage({
        text: "Please enter the complete 6-digit code",
        type: "error",
      });
      return;
    }

    setIsLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await fetch(`${API_URL}/api/member/verify-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: email,
          otp: enteredOtp,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setShowSuccessPopup(true);
        localStorage.removeItem("verifyEmail");

        try {
          const loginResponse = await fetch(
            `${API_URL}/api/member/login`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                email: email,
                password: localStorage.getItem("tempPassword"),
              }),
            }
          );

          const loginData = await loginResponse.json();

          if (loginResponse.ok) {
            localStorage.setItem("auth_token", loginData.access_token);
            localStorage.setItem("user_type", "member");
          }
        } catch (loginError) {
          console.log(
            "Auto-login failed, user will be redirected to sign in"
          );
        }
      } else {
        setMessage({
          text:
            data.message ||
            "Invalid verification code. Please try again.",
          type: "error",
        });
      }
    } catch (error) {
      setMessage({
        text: "Network error. Please check your connection and try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePopupClose = () => {
    setShowSuccessPopup(false);
    if (localStorage.getItem("auth_token")) {
      navigate("/member/wallet");
    } else {
      navigate("/member-login");
    }
  };

  const messageStyle = {
    textAlign: "center",
    padding: "10px",
    borderRadius: "8px",
    margin: "20px 0",
    fontWeight: "500",
  };

  return (
    <div className="verify-email-container">
      <Header />

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="reward-overlay">
          <div className="reward-popup">
            <div className="reward-icon" style={{ textAlign: "center" }}>
              🎉
            </div>
            <h2 className="reward-title" style={{ color: "white" }}>
              Account created successfully
            </h2>
            <p className="reward-message">
              We will review your account very soon
            </p>
            <p className="reward-label">Thank you for joining with us!</p>
            <button className="reward-button" onClick={handlePopupClose}>
              Continue
            </button>
          </div>
        </div>
      )}

      <div className="verify-email-content">
        <div className="verify-email-card">
          <h2 className="verify-email-heading">Verify your email</h2>
          <p className="verify-email-subheading">
            Please enter the 6 digit code sent to {email} to verify
          </p>

          {message.text && (
            <div
              style={{
                ...messageStyle,
                backgroundColor:
                  message.type === "error" ? "#ffebee" : "#e8f5e9",
                color: message.type === "error" ? "#c62828" : "#2e7d32",
              }}
            >
              {message.text}
            </div>
          )}

          <div className="otp-container">
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="otp-input"
                value={digit}
                onChange={(e) => handleChange(index, e)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : null}
                ref={(ref) => (inputRefs.current[index] = ref)}
                maxLength={1}
                disabled={isLoading}
              />
            ))}
          </div>

          <div className="resend-container">
            <div className="timer-container">
              {isResendDisabled && (
                <span className="timer-text">
                  Time remaining: {formatTime(timeLeft)}s
                </span>
              )}
            </div>
            <div className="resend-link-container">
              <span className="resend-prompt">Didn't get the code? </span>
              <button
                className={`resend-button ${
                  isResendDisabled ? "disabled" : ""
                }`}
                onClick={handleResend}
                disabled={isResendDisabled || isLoading}
              >
                {isLoading ? "Sending..." : "Resend"}
              </button>
            </div>
          </div>

          <div className="verify-button-container">
            <button
              className="verify-button"
              onClick={handleVerify}
              disabled={isLoading}
              style={{
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
            >
              {isLoading ? "Verifying..." : "Verify"}
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VerifyEmail;
